import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import GlobalStyle from "../../styles/global"
import styled, { ThemeProvider, StyleSheetManager } from "styled-components"
import "slick-carousel/slick/slick.css"
import "slick-carousel/slick/slick-theme.css"
import NewsletterLocationPopUp from "../Core/NewsletterLocationPopup"
import { BaseCSS, GridThemeProvider } from "styled-bootstrap-grid"
import { gsap } from "gsap"
import { CSSPlugin } from "gsap/CSSPlugin"
import { Helmet } from "react-helmet"
import { isBrowser } from "../../services/browser"
import SeoContent from "../Core/SeoContent"
import { FormContextProvider } from "../../context/form/FormContext"
import { PromotionContextProvider } from "../../context/PromotionContext"
import AccountPopup from "../Account/Forms/AccountPopup"
import BespokePopup from "../Bespoke/BespokePopup"
import { globalHistory } from "@reach/router"
import { cvgTrack } from "../../services/converge"
import Header from "./Header"
import Prefooter from "../Core/Prefooter/Prefooter"
import Footer from "./Footer"

gsap.registerPlugin(CSSPlugin)

const PageContainer = styled.div`
  width: 100%;
  flex-grow: 1;
`

const GlobalPageContainer = styled.div`
  display: flex;
  min-height: 100vh;
  flex-direction: column;
`

const gridTheme = {
  breakpoints: {
    xl: 1200,
    lg: 960,
    md: 600,
    sm: 576,
    xs: 575,
  },
  row: {
    padding: 0,
  },
  col: {
    padding: 0,
  },
  container: {
    padding: 0,
    maxWidth: {
      xl: 1600,
      lg: 1600,
      md: 1600,
      sm: 1600,
      xs: 1600,
    },
  },
}

const styledTheme = {}

const Layout = (props) => {
  const {
    children,
    isEngagement,
    prefooter,
    seoContent,
    discontinued,
    alternatePath,
    pdp,
    archive,
  } = props
  const [isAtTop, setIsAtTop] = useState(true)
  const [isAccountPopupOpen, setIsAccountPopupOpen] = useState(false)
  const [resetPasswordToken, setResetPasswordToken] = useState(null)
  const [isBespokeOpen, setIsBespokeOpen] = useState(false)
  const [signupEvent, setSignupEvent] = useState(null)
  const [formFillEvent, setFormFillEvent] = useState(null)

  const gtag_report_conversion = (url) => {
    const callback = () => {
      if (typeof url != "undefined") {
        window.location = url
      }
    }

    let gtag = window.gtag

    if (typeof gtag === "function") {
      gtag("event", "conversion", {
        send_to: "AW-781423112/DIHZCL-Qy6sDEIikzvQC",
        event_callback: callback,
      })
    }

    return false
  }

  useEffect(() => {
    if (isBrowser()) {
      if (
        window.location.search &&
        window.location.search.includes("reset-password-token")
      ) {
        const token = window.location.search.split("reset-password-token=")[1]
        setResetPasswordToken(token)
      }
    }
  }, [])

  useEffect(() => {
    if (isBrowser() && signupEvent) {
      const email = signupEvent.detail.metaData.$email
      cvgTrack({
        event: "Signed Up For Newsletter",
        properties: { ...signupEvent.detail.metaData, email: email },
        profileProperties: { $email: email },
        aliases: email ? [`urn:email:${email}`] : [],
      })

      const liQ = window.liQ || []
      liQ.push({
        event: "conversion",
        name: "email_signup",
        email: signupEvent.detail.metaData.$email,
      })
      setTimeout(() => {
        setSignupEvent(null)
      }, 100)
    }
  }, [signupEvent])

  useEffect(() => {
    if (isBrowser() && formFillEvent) {
      const liQ = window.liQ || []
      const emailData = formFillEvent.data.data.find(
        (data) => data.name === "email"
      )
      const userEmail = emailData ? emailData.value : ""

      // This is how you merge an array of objects into one object
      const mergedForm = formFillEvent.data.data.reduce((acc, curr) => {
        acc[curr.name] = curr.value
        return acc
      }, {})

      cvgTrack({
        event: "Submitted Form",
        properties: {
          email: userEmail,
          ...mergedForm,
        },
        profileProperties: {
          $email: userEmail,
        },
        aliases: userEmail ? [`urn:email:${userEmail}`] : [],
      })

      liQ.push({
        event: "conversion",
        name: "form_fill",
        email: userEmail,
      })

      gtag_report_conversion()

      setTimeout(() => {
        setFormFillEvent(null)
      }, 100)
    }
  }, [formFillEvent])

  useEffect(() => {
    if (isBrowser()) {
      window.addEventListener("scroll", handleScroll)
      window.addEventListener("klaviyoForms", function (e) {
        if (e.detail.type === "submit") {
          if (!signupEvent) {
            setSignupEvent(e)
          }
        }
      })
      window.addEventListener("message", (e) => {
        if (
          e.data.type === "hsFormCallback" &&
          e.data.eventName === "onFormSubmit"
        ) {
          if (!formFillEvent) {
            setFormFillEvent(e)
          }
        }
      })
    }
    return () => {
      if (isBrowser()) {
        window.removeEventListener("scroll", handleScroll)
      }
    }
  })

  const handleScroll = () => {
    let scrollTop = window.pageYOffset
    let atTop = true

    if (scrollTop > 1) {
      atTop = false
    }

    if (atTop !== isAtTop) {
      setIsAtTop(atTop)
    }
  }

  useEffect(() => {
    cvgTrack({ event: "$page_load" })
    return globalHistory.listen(({ action }) => {
      if (action === "PUSH") {
        cvgTrack({ event: "$page_load" })
        let liQ = window.liQ || []
        if (typeof liQ !== "undefined") {
          liQ.push({
            event: "pageView",
          })
        }
      }
    })
  }, [])

  const handleAccountPopup = (action) => {
    setIsAccountPopupOpen(action)
  }
  const closeBespokePopup = () => {
    setIsBespokeOpen(false)
  }
  return (
    <ThemeProvider theme={styledTheme}>
      <GridThemeProvider gridTheme={gridTheme}>
        <GlobalStyle />
        <BaseCSS />
        <FormContextProvider>
          <PromotionContextProvider>
            <StyleSheetManager shouldForwardProp={() => true}>
              <GlobalPageContainer>
                <Helmet>
                  <noscript>
                    {`<img height="1" width="1" style="display:none;" alt="" src="https://ct.pinterest.com/v3/?tid=2612695181356&noscript=1" />`}
                  </noscript>
                </Helmet>
                <Header
                  isBespokeOpen={isBespokeOpen}
                  setIsBespokeOpen={setIsBespokeOpen}
                  alternatePath={alternatePath}
                  isAccountPopupOpen={isAccountPopupOpen}
                  handleAccountPopup={handleAccountPopup}
                />
                <PageContainer>{children}</PageContainer>
                <NewsletterLocationPopUp alternatePath={alternatePath} />
                <AccountPopup
                  isOpen={isAccountPopupOpen}
                  handlePopup={handleAccountPopup}
                  resetPasswordToken={resetPasswordToken}
                />
                <BespokePopup
                  isOpen={isBespokeOpen}
                  close={closeBespokePopup}
                />
                {prefooter === true && (
                  <Prefooter
                    isEngagement={isEngagement ? 1 : 0}
                    discontinued={discontinued ? 1 : 0}
                    archive={archive ? 1 : 0}
                  />
                )}
                {seoContent && <SeoContent content={seoContent} />}
                <Footer alternatePath={alternatePath} pdp={pdp} />
              </GlobalPageContainer>
            </StyleSheetManager>
          </PromotionContextProvider>
        </FormContextProvider>
      </GridThemeProvider>
    </ThemeProvider>
  )
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
}

export default Layout
